import { FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { KeyRound, ShieldCheck, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { WarmMarketingBackdrop } from '../components/marketing/MarketingVisuals';
import { EnterpriseProofPack } from '../components/enterprise/EnterpriseProofPack';
import { EnterpriseSalesNav } from '../components/enterprise/EnterpriseSalesNav';
import { useAuth } from '../lib/auth-context';
import { listIdentityProviders, upsertIdentityProvider } from '../lib/enterprise-api';
import { useSEO } from '../lib/seo';
import { EnterpriseOrgSwitcher } from '../components/enterprise/EnterpriseOrgSwitcher';
import { useEnterpriseOrg } from '../context/EnterpriseOrgContext';

export default function EnterpriseIdentity() {
  useSEO({
    title: 'Enterprise Identity | TENSION for Work',
    description: 'Configure SSO-ready identity provider settings for enterprise organizations.',
    canonical: '/enterprise/identity',
    ogType: 'website',
  });
  const { accessToken } = useAuth();
  const { activeOrgId } = useEnterpriseOrg();
  const [orgId, setOrgId] = useState('');
  const [providerType, setProviderType] = useState<'saml' | 'oidc'>('saml');
  const [issuer, setIssuer] = useState('');
  const [audience, setAudience] = useState('');
  const [discoveryUrl, setDiscoveryUrl] = useState('');
  const [metadataUrl, setMetadataUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [providers, setProviders] = useState<Array<Record<string, unknown>>>([]);

  useEffect(() => {
    if (!orgId && activeOrgId) setOrgId(activeOrgId);
  }, [activeOrgId, orgId]);

  const load = async () => {
    if (!accessToken) return toast.error('Sign in required');
    if (!orgId.trim()) return toast.error('Enter org id');
    setLoading(true);
    const result = await listIdentityProviders(orgId.trim(), accessToken);
    setLoading(false);
    if (!result.success) return toast.error(result.error || 'Failed to load providers');
    setProviders(result.providers || []);
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (!accessToken) return toast.error('Sign in required');
    if (!orgId.trim()) return toast.error('Enter org id');
    setLoading(true);
    const result = await upsertIdentityProvider(
      orgId.trim(),
      { providerType, status: 'draft', issuer, audience, discoveryUrl, metadataUrl },
      accessToken,
    );
    setLoading(false);
    if (!result.success) return toast.error(result.error || 'Failed to save identity provider');
    toast.success('Identity provider saved');
    await load();
  };

  return (
    <WarmMarketingBackdrop>
      <EnterpriseSalesNav variant="light" />
      <div className="enterprise-public-shell mx-auto min-h-screen max-w-4xl space-y-6 px-4 py-10 md:py-14">
        <header className="enterprise-hero-shell">
          <div className="enterprise-hero-overlay" />
          <div className="enterprise-hero-content">
          <p className="inline-flex rounded-full bg-violet-100 px-3 py-1 text-xs font-bold uppercase tracking-[0.14em] text-violet-700">
            Identity scaffold
          </p>
          <h1 className="mt-3 text-3xl font-black tracking-tight text-slate-900 md:text-5xl">SSO-ready org identity</h1>
          <p className="mt-3 text-slate-600 md:text-lg">
            Configure SAML/OIDC provider metadata for pilot-to-production enterprise onboarding.
          </p>
          </div>
        </header>

        <Card className="enterprise-surface-card p-6">
          <form className="grid gap-3 md:grid-cols-2" onSubmit={save}>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="org-id">Organization ID</Label>
              <Input id="org-id" value={orgId} onChange={(e) => setOrgId(e.target.value)} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="provider-type">Provider type</Label>
              <select
                id="provider-type"
                value={providerType}
                onChange={(e) => setProviderType(e.target.value as 'saml' | 'oidc')}
                className="w-full rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm"
              >
                <option value="saml">SAML</option>
                <option value="oidc">OIDC</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="issuer">Issuer</Label>
              <Input id="issuer" value={issuer} onChange={(e) => setIssuer(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="audience">Audience</Label>
              <Input id="audience" value={audience} onChange={(e) => setAudience(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="discovery">Discovery URL</Label>
              <Input id="discovery" value={discoveryUrl} onChange={(e) => setDiscoveryUrl(e.target.value)} />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="metadata">Metadata URL</Label>
              <Input id="metadata" value={metadataUrl} onChange={(e) => setMetadataUrl(e.target.value)} />
            </div>
            <div className="md:col-span-2 flex flex-wrap gap-2">
              <Button type="submit" disabled={loading}>
                Save provider
              </Button>
              <Button type="button" variant="outline" onClick={load} disabled={loading}>
                Load providers
              </Button>
              <Link to="/enterprise/whitelabel">
                <Button type="button" variant="ghost">
                  White-label settings
                </Button>
              </Link>
            </div>
          </form>
        </Card>

        <Card className="enterprise-surface-card p-4">
          <EnterpriseOrgSwitcher />
          {activeOrgId && !orgId && (
            <button
              type="button"
              className="mt-3 text-xs text-violet-700 underline"
              onClick={() => setOrgId(activeOrgId)}
            >
              Use active org id in form
            </button>
          )}
        </Card>

        <Card className="enterprise-surface-card p-6">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-900">
            <ShieldCheck className="h-5 w-5 text-violet-600" aria-hidden />
            Configured providers
          </h2>
          <ul className="mt-3 space-y-2 text-sm text-slate-600">
            {providers.length === 0 && <li>No providers saved yet.</li>}
            {providers.map((p, idx) => (
              <li key={`${String(p.id || idx)}`} className="rounded-md border border-violet-100 px-3 py-2">
                <span className="font-medium">{String(p.provider_type || 'unknown').toUpperCase()}</span> ·{' '}
                <span>{String(p.status || 'draft')}</span>
              </li>
            ))}
          </ul>
          <p className="mt-3 inline-flex items-center gap-2 text-xs text-slate-500">
            <KeyRound className="h-3.5 w-3.5" aria-hidden />
            Sensitive keys remain in secure config; this page stores metadata scaffolding only.
          </p>
          <p className="enterprise-meta-pill mt-2">
            <Sparkles className="h-3.5 w-3.5" aria-hidden />
            Enterprise identity controls are ready for gradual rollout.
          </p>
        </Card>

        <EnterpriseProofPack compact />
      </div>
    </WarmMarketingBackdrop>
  );
}
